import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  AccountCircle as AccountCircleIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
  MenuBook as MenuBookIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';

const ALL_PATH_NAV = [
  { text: 'My Learning', icon: <MenuBookIcon />, to: '/my-learning', paths: null, authRequired: true },
  { text: 'Cohort', icon: <GroupsIcon />, to: '/cohort', paths: null, authRequired: true },
  { text: 'Admin', icon: <AdminPanelSettingsIcon />, to: '/admin', paths: null, adminRequired: true },
  { text: 'About', icon: <InfoIcon />, to: '/about', paths: null },
];

const getNavItems = (learningPaths = [], isLoggedIn = false, role = 'learner') =>
  ALL_PATH_NAV.filter((item) => {
    if (item.authRequired && !isLoggedIn) return false;
    if (item.adminRequired && !['admin', 'platform_owner'].includes(role)) return false;
    return item.paths === null || item.paths.some((p) => learningPaths.includes(p));
  });

const Header = ({ onOpenMenu, onOpenSearch, onOpenAuth, onOpenOnboarding, onOpenAccount }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { user, logout } = useAuth();
  const navItems = getNavItems(user?.learning_paths, Boolean(user), user?.role);
  const isActiveNav = (item) =>
    location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(`${item.to}/`));

  return (
    <AppBar position="fixed" elevation={1} sx={{ bgcolor: theme.palette.primary.main, color: '#fff' }}>
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
          {/* Left — logo + mobile menu toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <IconButton edge="start" sx={{ mr: 1, color: 'inherit' }} aria-label="menu" onClick={onOpenMenu}>
                <MenuIcon />
              </IconButton>
            )}
            <Box
              component={RouterLink}
              to="/"
              aria-label="Qode Clarity home"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { opacity: 0.87 }
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Qode Clarity"
                sx={{
                  display: 'block',
                  width: { xs: 34, sm: 38 },
                  height: { xs: 34, sm: 38 },
                  objectFit: 'contain',
                  objectPosition: 'center'
                }}
              />
              <Typography
                component="span"
                sx={{
                  display: { xs: 'none', sm: 'inline' },
                  fontWeight: 800,
                  fontSize: '1rem',
                  lineHeight: 1,
                  letterSpacing: 0
                }}
              >
                Qode Clarity
              </Typography>
            </Box>
          </Box>

          {/* Centre — navigation links (desktop only) */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {navItems.map((item) => {
                const active = isActiveNav(item);
                return (
                  <Button
                    key={item.text}
                    component={RouterLink}
                    to={item.to}
                    startIcon={item.icon}
                    aria-current={active ? 'page' : undefined}
                    sx={{
                      position: 'relative',
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: active ? 900 : 600,
                      color: '#fff',
                      bgcolor: active ? alpha('#fff', 0.18) : 'transparent',
                      boxShadow: active ? `inset 0 -3px 0 ${alpha('#fff', 0.95)}` : 'none',
                      '& .MuiButton-startIcon': {
                        color: active ? '#fff' : alpha('#fff', 0.9),
                      },
                      '&:hover': {
                        bgcolor: active ? alpha('#fff', 0.22) : alpha('#fff', 0.1),
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Right — search + auth */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton
              aria-label="search"
              onClick={onOpenSearch}
              sx={{ color: 'inherit', '&:hover': { bgcolor: alpha('#fff', 0.1) } }}
            >
              <SearchIcon />
            </IconButton>
            {user && (
              <Button
                size="small"
                onClick={onOpenOnboarding}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  color: 'inherit',
                  border: `1px solid ${alpha('#fff', 0.35)}`,
                  '&:hover': { bgcolor: alpha('#fff', 0.1) }
                }}
              >
                How this works
              </Button>
            )}
            {user ? (
              <>
                <Button
                  size="small"
                  startIcon={<AccountCircleIcon />}
                  onClick={onOpenAccount}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    color: 'inherit',
                    textTransform: 'none',
                    px: 1,
                    '&:hover': { bgcolor: alpha('#fff', 0.1) }
                  }}
                >
                  {user.first_name}
                </Button>
                <Button
                  size="small"
                  onClick={logout}
                  sx={{
                    color: 'inherit',
                    border: `1px solid ${alpha('#fff', 0.4)}`,
                    '&:hover': { bgcolor: alpha('#fff', 0.1) }
                  }}
                >
                  Sign out
                </Button>
              </>
            ) : (
              <Button
                size="small"
                onClick={onOpenAuth}
                sx={{
                  color: 'inherit',
                  border: `1px solid ${alpha('#fff', 0.5)}`,
                  '&:hover': { bgcolor: alpha('#fff', 0.1) }
                }}
              >
                Sign in
              </Button>
            )}
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
